import React, { useEffect, useState } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Container,
    Grid,
    Paper,
    useTheme,
    useMediaQuery,
    Input,
    IconButton
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const features = [
    {
        num: "01",
        icon: "🛡",
        title: "Visitor Management",
        desc: "Register visitors, approve entries and maintain secure digital visitor logs.",
        tag: "Visitors"
    },
    {
        num: "02",
        icon: "🔧",
        title: "Society Management",
        desc: "Manage residents, tenants and community members efficiently.",
        tag: "Community"
    },
    {
        num: "03",
        icon: "📝",
        title: "Templates Management",
        desc: "Create reusable templates for visitor passes and approvals.",
        tag: "Templates"
    },
    {
        num: "04",
        icon: "👥",
        title: "Real Time Tracking",
        desc: "Track visitor movement and facility activity in real time.",
        tag: "Tracking"
    },
    {
        num: "05",
        icon: "🏢",
        title: "Approval Workflows",
        desc: "Automate approvals with multi-level authorization flows.",
        tag: "Automation"
    },
    {
        num: "06",
        icon: "🔔",
        title: "Intelligent Ops",
        desc: "Tracks Warranties, AMC Schedules, Contracts and Memberships",
        tag: "Operations"
    }
];

const tickerItems = [
    "VISITOR MANAGEMENT",
    "SOCIETY MANAGEMENT",
    "TEMPLATES MANAGEMENT",
    "REAL TIME TRACKING",
    "APPROVAL WORKFLOWS",
    "INTELLIGENT OPERATIONS"
];

const faqs = [
    {
        q: "Is Maintain really free to start?",
        a: "Yes. Our Starter plan is free forever for up to 3 users with 50 warranty records. No credit card required. You only pay when you need more scale."
    },
    {
        q: "How does the OTP login work?",
        a: "Enter your email, receive a one-time code, and you're in. No passwords to remember or manage. Works with Google too."
    },
    {
        q: "Can I manage multiple locations?",
        a: "Absolutely. The Facility Management module lets you create separate spaces for each site, assign admins, and view everything from a central dashboard."
    },
    {
        q: "What kind of alerts does Maintain send?",
        a: "Email alerts and in-app notifications at configurable thresholds — typically 90, 60, and 30 days before any warranty, contract, or AMC deadline."
    }
];

const HomePage = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [openFaq, setOpenFaq] = useState(0);
    const [email, setEmail] = useState("");
    const [showLogin, setShowLogin] = useState(false);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? -1 : index);
    };

    const styles = {
        root: {
            fontFamily: "'Epilogue', sans-serif",
            backgroundColor: "#fefce8",
            color: "#1c1400",
            overflowX: "hidden"
        },
        unbounded: {
            fontFamily: "'Unbounded', sans-serif"
        },
        nav: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "0 5vw",
            background: "rgba(254,252,232,.94)",
            backdropFilter: "blur(14px)",
            borderBottom: "2px solid #f0d060"
        },
        logo: {
            fontSize: "1.1rem",
            fontWeight: 900,
            letterSpacing: "-0.03em"
        },
        hero: {
            minHeight: "100vh",
            pt: "110px",
            pb: "80px",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center"
        },
        heroContent: {
            position: "relative",
            zIndex: 1,
            maxWidth: "780px"
        },
        h1: {
            fontSize: { xs: "2.2rem", md: "4.6rem" },
            fontWeight: 900,
            lineHeight: 1.05,
            letterSpacing: "-0.04em",
            mb: 3
        },
        gold: {
            color: "#f59e0b !important"
        },
        ticker: {
            backgroundColor: "#1c1400",
            p: "14px 0",
            overflow: "hidden",
            borderTop: "2px solid #f0d060",
            borderBottom: "2px solid #f0d060"
        },
        tickerInner: {
            display: "flex",
            gap: "48px",
            animation: "scroll 20s linear infinite",
            whiteSpace: "nowrap"
        },
        fc: {
            backgroundColor: "#ffffff",
            border: "2px solid #f0d060",
            borderRadius: "12px",
            p: 3,
            transition: "all .2s",
            position: "relative",
            overflow: "hidden",
            "&:hover": {
                borderColor: "#f59e0b",
                backgroundColor: "#fefce8",
                transform: "translateY(-3px)",
                boxShadow: "0 8px 32px rgba(202,138,4,.12)"
            }
        },
        loginWrap: {
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            minHeight: "100vh"
        }
    };

    return (
        <Box sx={styles.root}>
            {/* NAVBAR */}
            <Box sx={styles.nav}>
                <Typography sx={{ ...styles.logo, ...styles.unbounded }}>
                    AALAY
                    <Box component="span" sx={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "#f59e0b",
                        ml: 0.5,
                        verticalAlign: "middle"
                    }} />
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>

                    {isAuthenticated ? (

                        <Button
                            sx={{
                                p: "9px 22px",
                                borderRadius: "6px",
                                backgroundColor: "#1c1400",
                                border: "2px solid #1c1400",
                                color: "#fde047",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                letterSpacing: "-0.02em",
                                fontFamily: "'Unbounded', sans-serif",
                                "&:hover": {
                                    backgroundColor: "#f59e0b",
                                    borderColor: "#f59e0b",
                                    color: "#1c1400"
                                }
                            }}
                            onClick={() => navigate("/dashboard")}
                        >
                            GO TO DASHBOARD →
                        </Button>

                    ) : (

                        <>
                            <Button
                                sx={{
                                    p: "8px 18px",
                                    borderRadius: "6px",
                                    border: "2px solid #f0d60",
                                    background: "transparent",
                                    color: "#3d2e00",
                                    fontSize: "0.8rem",
                                    fontWeight: 500,
                                    "&:hover": {
                                        borderColor: "#f59e0b",
                                        color: "#ca8a04"
                                    }
                                }}
                                onClick={() => navigate("/login")}
                            >
                                Sign in
                            </Button>

                            <Button
                                sx={{
                                    p: "9px 22px",
                                    borderRadius: "6px",
                                    backgroundColor: "#1c1400",
                                    border: "2px solid #1c1400",
                                    color: "#fde047",
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    letterSpacing: "-0.02em",
                                    fontFamily: "'Unbounded', sans-serif",
                                    "&:hover": {
                                        backgroundColor: "#f59e0b",
                                        borderColor: "#f59e0b",
                                        color: "#1c1400"
                                    }
                                }}
                                onClick={() => navigate("/signup")}
                            >
                                GET STARTED →
                            </Button>
                        </>

                    )}

                </Box>
            </Box>

            {!showLogin ? (
                <>
                    {/* HERO */}
                    <Box sx={styles.hero}>
                        {/* SVG Cityscape Background */}
                        <Box sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 0,
                            pointerEvents: "none",
                            overflow: "hidden"
                        }}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 1440 700"
                                preserveAspectRatio="xMidYMax slice"
                                style={{ width: "100%", height: "100%", position: "absolute", bottom: 0, left: 0 }}
                            >
                                {/* Sky gradient */}
                                <defs>
                                    <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#fefce8" stopOpacity="1" />
                                        <stop offset="100%" stopColor="#fde047" stopOpacity="0.18" />
                                    </linearGradient>
                                    <linearGradient id="buildingGold" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#ca8a04" stopOpacity="0.22" />
                                        <stop offset="100%" stopColor="#b45309" stopOpacity="0.18" />
                                    </linearGradient>
                                    <linearGradient id="buildingDark" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#1c1400" stopOpacity="0.13" />
                                        <stop offset="100%" stopColor="#3d2e00" stopOpacity="0.1" />
                                    </linearGradient>
                                    <linearGradient id="tallCenter" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#1c1400" stopOpacity="0.16" />
                                        <stop offset="100%" stopColor="#ca8a04" stopOpacity="0.12" />
                                    </linearGradient>
                                </defs>

                                {/* Sky */}
                                <rect width="1440" height="700" fill="url(#skyGrad)" />

                                {/* Clouds */}
                                <ellipse cx="160" cy="100" rx="130" ry="48" fill="#fde047" opacity="0.2" />
                                <ellipse cx="280" cy="80" rx="95" ry="38" fill="#fde047" opacity="0.15" />
                                <ellipse cx="380" cy="105" rx="75" ry="30" fill="#fde047" opacity="0.12" />
                                <ellipse cx="1050" cy="85" rx="140" ry="50" fill="#fde047" opacity="0.18" />
                                <ellipse cx="1200" cy="70" rx="110" ry="42" fill="#fde047" opacity="0.14" />
                                <ellipse cx="1330" cy="100" rx="85" ry="32" fill="#fde047" opacity="0.12" />

                                {/* ── FAR BACKGROUND LAYER (lightest, smallest) ── */}
                                <rect x="0" y="430" width="60" height="270" fill="#f0d060" opacity="0.12" />
                                <rect x="55" y="400" width="50" height="300" fill="#f0d060" opacity="0.1" />
                                <rect x="100" y="440" width="55" height="260" fill="#f0d060" opacity="0.12" />
                                <rect x="200" y="420" width="45" height="280" fill="#f0d060" opacity="0.1" />
                                <rect x="240" y="390" width="60" height="310" fill="#f0d060" opacity="0.11" />
                                <rect x="1130" y="400" width="55" height="300" fill="#f0d060" opacity="0.11" />
                                <rect x="1180" y="430" width="60" height="270" fill="#f0d060" opacity="0.12" />
                                <rect x="1280" y="410" width="55" height="290" fill="#f0d060" opacity="0.1" />
                                <rect x="1340" y="380" width="100" height="320" fill="#f0d060" opacity="0.12" />

                                {/* ── MID LAYER LEFT ── */}
                                {/* Short wide block */}
                                <rect x="0" y="430" width="95" height="270" fill="url(#buildingGold)" />
                                {/* Stepped block */}
                                <rect x="85" y="390" width="70" height="310" fill="url(#buildingDark)" />
                                <rect x="85" y="380" width="70" height="15" fill="#f59e0b" opacity="0.25" />
                                {/* Windows */}
                                <rect x="93" y="400" width="9" height="7" fill="#fde047" opacity="0.45" />
                                <rect x="108" y="400" width="9" height="7" fill="#fde047" opacity="0.3" />
                                <rect x="123" y="400" width="9" height="7" fill="#fde047" opacity="0.5" />
                                <rect x="93" y="416" width="9" height="7" fill="#fde047" opacity="0.3" />
                                <rect x="108" y="416" width="9" height="7" fill="#fde047" opacity="0.5" />
                                <rect x="123" y="416" width="9" height="7" fill="#fde047" opacity="0.4" />
                                <rect x="93" y="432" width="9" height="7" fill="#fde047" opacity="0.5" />
                                <rect x="108" y="432" width="9" height="7" fill="#fde047" opacity="0.35" />
                                <rect x="123" y="432" width="9" height="7" fill="#fde047" opacity="0.45" />

                                {/* Medium tower left */}
                                <rect x="148" y="340" width="80" height="360" fill="url(#buildingGold)" />
                                <rect x="153" y="333" width="70" height="12" fill="#f59e0b" opacity="0.3" />
                                <line x1="188" y1="295" x2="188" y2="335" stroke="#ca8a04" strokeWidth="2" opacity="0.35" />
                                {/* Windows */}
                                <rect x="158" y="355" width="10" height="8" fill="#fde047" opacity="0.45" />
                                <rect x="174" y="355" width="10" height="8" fill="#fde047" opacity="0.3" />
                                <rect x="190" y="355" width="10" height="8" fill="#fde047" opacity="0.5" />
                                <rect x="206" y="355" width="10" height="8" fill="#fde047" opacity="0.35" />
                                <rect x="158" y="372" width="10" height="8" fill="#fde047" opacity="0.3" />
                                <rect x="174" y="372" width="10" height="8" fill="#fde047" opacity="0.5" />
                                <rect x="190" y="372" width="10" height="8" fill="#fde047" opacity="0.4" />
                                <rect x="206" y="372" width="10" height="8" fill="#fde047" opacity="0.45" />
                                <rect x="158" y="389" width="10" height="8" fill="#fde047" opacity="0.5" />
                                <rect x="174" y="389" width="10" height="8" fill="#fde047" opacity="0.35" />
                                <rect x="190" y="389" width="10" height="8" fill="#fde047" opacity="0.45" />
                                <rect x="206" y="389" width="10" height="8" fill="#fde047" opacity="0.3" />

                                {/* Wide building */}
                                <rect x="220" y="380" width="100" height="320" fill="url(#buildingDark)" />
                                <rect x="225" y="372" width="90" height="12" fill="#f59e0b" opacity="0.22" />
                                {/* Windows */}
                                <rect x="230" y="392" width="11" height="8" fill="#fde047" opacity="0.4" />
                                <rect x="248" y="392" width="11" height="8" fill="#fde047" opacity="0.5" />
                                <rect x="266" y="392" width="11" height="8" fill="#fde047" opacity="0.35" />
                                <rect x="284" y="392" width="11" height="8" fill="#fde047" opacity="0.45" />
                                <rect x="230" y="409" width="11" height="8" fill="#fde047" opacity="0.5" />
                                <rect x="248" y="409" width="11" height="8" fill="#fde047" opacity="0.3" />
                                <rect x="266" y="409" width="11" height="8" fill="#fde047" opacity="0.45" />
                                <rect x="284" y="409" width="11" height="8" fill="#fde047" opacity="0.4" />
                                <rect x="230" y="426" width="11" height="8" fill="#fde047" opacity="0.35" />
                                <rect x="248" y="426" width="11" height="8" fill="#fde047" opacity="0.5" />
                                <rect x="266" y="426" width="11" height="8" fill="#fde047" opacity="0.4" />
                                <rect x="284" y="426" width="11" height="8" fill="#fde047" opacity="0.3" />

                                {/* ── CENTER-LEFT TALL TOWER ── */}
                                <rect x="315" y="220" width="100" height="480" fill="url(#buildingGold)" />
                                <rect x="320" y="212" width="90" height="13" fill="#f59e0b" opacity="0.35" />
                                <line x1="365" y1="170" x2="365" y2="214" stroke="#f59e0b" strokeWidth="2.5" opacity="0.4" />
                                {/* Windows */}
                                {[235, 252, 269, 286, 303, 320, 337, 354].map((y, ri) =>
                                    [328, 346, 364, 382, 396].map((x, ci) => (
                                        <rect key={`cl-${ri}-${ci}`} x={x} y={y} width="11" height="8"
                                            fill="#fde047" opacity={[0.45, 0.3, 0.5, 0.35, 0.45][ci]} />
                                    ))
                                )}

                                {/* ── CENTER BUILDINGS ── */}
                                <rect x="418" y="300" width="90" height="400" fill="url(#buildingDark)" />
                                <rect x="423" y="292" width="80" height="12" fill="#f59e0b" opacity="0.28" />
                                <rect x="500" y="330" width="75" height="370" fill="url(#buildingGold)" />

                                {/* ── TALLEST CENTER TOWER ── */}
                                <rect x="572" y="130" width="120" height="570" fill="url(#tallCenter)" />
                                <rect x="577" y="122" width="110" height="14" fill="#f59e0b" opacity="0.45" />
                                <line x1="632" y1="75" x2="632" y2="124" stroke="#f59e0b" strokeWidth="3" opacity="0.5" />
                                <circle cx="632" cy="73" r="4" fill="#f59e0b" opacity="0.6" />
                                {/* Windows tall center */}
                                {[144, 162, 180, 198, 216, 234, 252, 270, 288, 306, 324].map((y, ri) =>
                                    [582, 600, 618, 636, 654, 672].map((x, ci) => (
                                        <rect key={`tc-${ri}-${ci}`} x={x} y={y} width="12" height="9"
                                            fill="#fde047" opacity={[0.5, 0.35, 0.5, 0.3, 0.45, 0.4][ci]} />
                                    ))
                                )}

                                {/* ── CENTER-RIGHT BUILDINGS ── */}
                                <rect x="695" y="270" width="95" height="430" fill="url(#buildingGold)" />
                                <rect x="700" y="262" width="85" height="13" fill="#f59e0b" opacity="0.3" />
                                <line x1="742" y1="222" x2="742" y2="264" stroke="#ca8a04" strokeWidth="2" opacity="0.38" />
                                {/* Windows */}
                                {[280, 297, 314, 331, 348, 365].map((y, ri) =>
                                    [705, 721, 737, 753, 769].map((x, ci) => (
                                        <rect key={`cr-${ri}-${ci}`} x={x} y={y} width="10" height="8"
                                            fill="#fde047" opacity={[0.4, 0.5, 0.3, 0.45, 0.35][ci]} />
                                    ))
                                )}

                                <rect x="782" y="310" width="80" height="390" fill="url(#buildingDark)" />

                                {/* ── CURVED / GLASS BUILDING ── */}
                                <rect x="864" y="240" width="105" height="460" fill="url(#buildingGold)" />
                                <ellipse cx="916" cy="240" rx="52" ry="18" fill="#ca8a04" opacity="0.15" />
                                <rect x="869" y="246" width="95" height="12" fill="#f59e0b" opacity="0.25" />
                                {/* Horizontal stripe accents */}
                                {[280, 310, 340, 370, 400, 430, 460].map((y, i) => (
                                    <rect key={`stripe-${i}`} x="864" y={y} width="105" height="2"
                                        fill="#f59e0b" opacity="0.12" />
                                ))}
                                {/* Windows */}
                                {[260, 278, 296, 314, 332, 350].map((y, ri) =>
                                    [874, 891, 908, 925, 942, 959].map((x, ci) => (
                                        <rect key={`cv-${ri}-${ci}`} x={x} y={y} width="10" height="8"
                                            fill="#fde047" opacity={[0.45, 0.3, 0.5, 0.35, 0.45, 0.3][ci]} />
                                    ))
                                )}

                                {/* ── RIGHT BUILDINGS ── */}
                                <rect x="970" y="300" width="85" height="400" fill="url(#buildingDark)" />
                                <rect x="975" y="292" width="75" height="12" fill="#f59e0b" opacity="0.28" />
                                <rect x="1045" y="265" width="100" height="435" fill="url(#buildingGold)" />
                                <rect x="1050" y="257" width="90" height="13" fill="#f59e0b" opacity="0.35" />
                                <line x1="1095" y1="215" x2="1095" y2="259" stroke="#f59e0b" strokeWidth="2" opacity="0.4" />
                                {/* Windows */}
                                {[275, 292, 309, 326, 343, 360, 377].map((y, ri) =>
                                    [1055, 1071, 1087, 1103, 1119].map((x, ci) => (
                                        <rect key={`rb-${ri}-${ci}`} x={x} y={y} width="10" height="8"
                                            fill="#fde047" opacity={[0.5, 0.35, 0.5, 0.3, 0.45][ci]} />
                                    ))
                                )}

                                <rect x="1138" y="285" width="90" height="415" fill="url(#buildingDark)" />
                                <rect x="1143" y="277" width="80" height="13" fill="#f59e0b" opacity="0.25" />
                                {/* Windows */}
                                {[295, 312, 329, 346, 363].map((y, ri) =>
                                    [1148, 1164, 1180, 1196, 1208].map((x, ci) => (
                                        <rect key={`rb2-${ri}-${ci}`} x={x} y={y} width="10" height="8"
                                            fill="#fde047" opacity={[0.4, 0.5, 0.3, 0.45, 0.35][ci]} />
                                    ))
                                )}

                                {/* ── FAR RIGHT ── */}
                                <rect x="1228" y="320" width="80" height="380" fill="url(#buildingGold)" />
                                <rect x="1298" y="275" width="95" height="425" fill="url(#buildingDark)" />
                                <rect x="1303" y="267" width="85" height="12" fill="#f59e0b" opacity="0.3" />
                                <line x1="1345" y1="228" x2="1345" y2="269" stroke="#ca8a04" strokeWidth="2" opacity="0.35" />
                                {/* Windows far right */}
                                {[282, 299, 316, 333, 350].map((y, ri) =>
                                    [1308, 1324, 1340, 1356, 1372].map((x, ci) => (
                                        <rect key={`fr-${ri}-${ci}`} x={x} y={y} width="10" height="8"
                                            fill="#fde047" opacity={[0.45, 0.3, 0.5, 0.35, 0.45][ci]} />
                                    ))
                                )}
                                <rect x="1385" y="350" width="55" height="350" fill="url(#buildingGold)" />

                                {/* ── GROUND STRIP ── */}
                                <rect x="0" y="680" width="1440" height="20" fill="#1c1400" opacity="0.08" />
                                <rect x="0" y="672" width="1440" height="10" fill="#f59e0b" opacity="0.12" />
                            </svg>
                        </Box>

                        <Container sx={styles.heroContent}>
                            <Box sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                p: "6px 14px",
                                borderRadius: "6px",
                                backgroundColor: "#1c1400",
                                color: "#fde047",
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                mb: 3,
                                fontFamily: "'Unbounded', sans-serif"
                            }}>
                                <Box sx={{
                                    width: "7px",
                                    height: "7px",
                                    borderRadius: "50%",
                                    backgroundColor: "#f59e0b",
                                    animation: "blink 1.5s infinite"
                                }} />
                                AALAY · Live
                            </Box>

                            <Typography sx={styles.h1}>
                                The Gateway to<br />
                                <span sx={styles.gold}>Your Workspace.</span><br />
                            </Typography>

                            <Typography sx={{
                                fontSize: "1.05rem",
                                color: "#3d2e00",
                                lineHeight: 1.75,
                                maxWidth: "540px",
                                mb: 4,
                                fontWeight: 300
                            }}>
                                AALAY is an platform designed to manage visitors,
                                residents and facility operations across commercial,
                                residential and mixed facilities.
                            </Typography>

                            {!isAuthenticated && (
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                                    <Button
                                        sx={{
                                            p: "15px 36px",
                                            borderRadius: "6px",
                                            backgroundColor: "#1c1400",
                                            border: "2px solid #1c1400",
                                            color: "#fde047",
                                            fontSize: "0.8rem",
                                            fontWeight: 700,
                                            letterSpacing: "-0.02em",
                                            fontFamily: "'Unbounded', sans-serif",
                                            transition: "all .25s",
                                            "&:hover": {
                                                backgroundColor: "#f59e0b",
                                                borderColor: "#f59e0b",
                                                color: "#1c1400",
                                                transform: "translateY(-2px)"
                                            }
                                        }}
                                        onClick={() => navigate("/signup")}
                                    >
                                        GET STARTED
                                    </Button>
                                </Box>
                            )}
                        </Container>
                    </Box>

                    {/* TICKER */}
                    <Box sx={styles.ticker}>
                        <Box sx={{
                            ...styles.tickerInner,
                            "@keyframes scroll": {
                                from: { transform: "translateX(0)" },
                                to: { transform: "translateX(-50%)" }
                            }
                        }}>
                            {[...tickerItems, ...tickerItems].map((item, i) => (
                                <Typography key={i} sx={{
                                    fontFamily: "'Unbounded', sans-serif",
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    color: "#fde047",
                                    letterSpacing: "0.04em",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    flexShrink: 0
                                }}>
                                    <Box sx={{
                                        width: "5px",
                                        height: "5px",
                                        borderRadius: "50%",
                                        backgroundColor: "#f59e0b"
                                    }} />
                                    {item}
                                </Typography>
                            ))}
                        </Box>
                    </Box>

                    {/* FEATURES */}
                    <Container sx={{ py: 10 }}>
                        <Box sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            p: "5px 13px",
                            borderRadius: "4px",
                            backgroundColor: "#fde047",
                            color: "#1c1400",
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            mb: 2,
                            fontFamily: "'Unbounded', sans-serif"
                        }}>
                            What we do
                        </Box>

                        <Typography sx={{
                            ...styles.unbounded,
                            fontSize: { xs: "1.7rem", md: "2.8rem" },
                            fontWeight: 900,
                            letterSpacing: "-0.035em",
                            lineHeight: 1.1,
                            mb: 2
                        }}>
                            Six modules.<br />
                            <span sx={styles.gold}>Zero gaps.</span>
                        </Typography>

                        <Typography sx={{
                            fontSize: "0.95rem",
                            color: "#3d2e00",
                            maxWidth: "480px",
                            lineHeight: 1.72,
                            fontWeight: 300,
                            mb: 6
                        }}>
                            Everything you need to manage visitors, residents and facilities — all in one intelligent platform.
                        </Typography>

                        <Grid container spacing={2}>
                            {features.map((f, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Box sx={styles.fc} data-num={f.num}>
                                        <Box sx={{
                                            width: "44px",
                                            height: "44px",
                                            borderRadius: "8px",
                                            backgroundColor: "#fde047",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "1.2rem",
                                            mb: 2,
                                            border: "2px solid #f0d060"
                                        }}>
                                            {f.icon}
                                        </Box>
                                        <Typography sx={{
                                            ...styles.unbounded,
                                            fontSize: "0.88rem",
                                            fontWeight: 700,
                                            mb: 1,
                                            letterSpacing: "-0.025em",
                                            lineHeight: 1.3
                                        }}>
                                            {f.title}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: "0.82rem",
                                            color: "#3d2e00",
                                            lineHeight: 1.65,
                                            fontWeight: 300
                                        }}>
                                            {f.desc}
                                        </Typography>
                                        <Box sx={{
                                            display: "inline-block",
                                            mt: 2,
                                            p: "3px 10px",
                                            borderRadius: "4px",
                                            fontFamily: "'Unbounded', sans-serif",
                                            fontSize: "0.55rem",
                                            fontWeight: 700,
                                            letterSpacing: "0.08em",
                                            textTransform: "uppercase",
                                            backgroundColor: f.tag === "Coverage" || f.tag === "Members" ? "#fde047" :
                                                f.tag === "Maintenance" || f.tag === "Facilities" ? "#ccfbf1" : "#ede9fe",
                                            color: f.tag === "Coverage" || f.tag === "Members" ? "#1c1400" : "#7c3aed",
                                            border: f.tag !== "Coverage" && f.tag !== "Members" ? "1px solid" : "none",
                                            borderColor: f.tag === "Maintenance" ? "#a7f3d0" : "#c4b5fd"
                                        }}>
                                            {f.tag}
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>

                    {/* HOW IT WORKS */}
                    <Box sx={{
                        backgroundColor: "#1c1400",
                        borderTop: "2px solid #f0d060",
                        pt: 12,
                        pb: 8
                    }}>
                        <Container>
                            <Box sx={{ textAlign: "center", mb: 6 }}>
                                <Box sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: "14px",
                                    py: "5px",
                                    borderRadius: "4px",
                                    backgroundColor: "#fde047",
                                    color: "#1c1400",
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    fontFamily: "'Unbounded', sans-serif",
                                    margin: "0 auto 18px"
                                }}>
                                    <Box sx={{
                                        width: "6px",
                                        height: "6px",
                                        borderRadius: "50%",
                                        backgroundColor: "#f59e0b"
                                    }} />
                                    HOW IT WORKS
                                </Box>
                                <Typography sx={{
                                    ...styles.unbounded,
                                    fontSize: { xs: "1.7rem", md: "2.8rem" },
                                    fontWeight: 900,
                                    color: "#fde047",
                                    mb: 2
                                }}>
                                    Up and running<br />in three steps.
                                </Typography>
                                <Typography sx={{
                                    color: "rgba(255,255,255,.5)",
                                    fontSize: "0.9rem",
                                    maxWidth: "420px",
                                    mx: "auto",
                                    fontWeight: 300,
                                    lineHeight: 1.7
                                }}>
                                    Simple to set up, easy to use and designed for real-world facility operations.
                                </Typography>
                            </Box>

                            <Grid container spacing={0}>
                                {[
                                    {
                                        num: "01",
                                        icon: "🔐",
                                        title: "Set up your facility",
                                        desc: "Configure facilities, tenants and user roles within minutes."
                                    },
                                    {
                                        num: "02",
                                        icon: "📦",
                                        title: "Manage visitors",
                                        desc: "Register visitors and automate approval workflows."
                                    },
                                    {
                                        num: "03",
                                        icon: "⚡",
                                        title: "Track activity",
                                        desc: "Monitor visitor movement and operations in real time."
                                    }
                                ].map((step, i) => (
                                    <Grid item xs={12} md={4} key={i} sx={{ px: 4 }}>
                                        <Box sx={{
                                            py: 5,
                                            borderRight: i < 2 ? "1px solid rgba(255,255,255,.08)" : "none"
                                        }}>
                                            <Typography sx={{
                                                ...styles.unbounded,
                                                fontSize: "4rem",
                                                fontWeight: 900,
                                                color: "rgba(253,224,71,.12)",
                                                lineHeight: 1,
                                                mb: 2
                                            }}>
                                                {step.num}
                                            </Typography>
                                            <Box sx={{
                                                width: "48px",
                                                height: "48px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fde047",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "1.3rem",
                                                mb: 2
                                            }}>
                                                {step.icon}
                                            </Box>
                                            <Typography sx={{
                                                ...styles.unbounded,
                                                fontSize: "0.9rem",
                                                fontWeight: 700,
                                                color: "#fde047",
                                                mb: 1,
                                                letterSpacing: "-0.02em",
                                                lineHeight: 1.3
                                            }}>
                                                {step.title}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: "0.83rem",
                                                color: "rgba(255,255,255,.5)",
                                                lineHeight: 1.65,
                                                fontWeight: 300
                                            }}>
                                                {step.desc}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Container>
                    </Box>

                    {/* FAQ
                    <Box sx={{
                        backgroundColor: "#fef9c3",
                        borderTop: "2px solid #f0d060",
                        py: 12
                    }}>
                        <Container sx={{ maxWidth: "700px" }}>
                            <Box sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                p: "5px 13px",
                                borderRadius: "4px",
                                backgroundColor: "#fde047",
                                color: "#1c1400",
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                mb: 2,
                                fontFamily: "'Unbounded', sans-serif"
                            }}>
                                FAQ
                            </Box>

                            <Typography sx={{
                                ...styles.unbounded,
                                fontSize: { xs: "1.7rem", md: "2.8rem" },
                                fontWeight: 900,
                                letterSpacing: "-0.035em",
                                lineHeight: 1.1,
                                mb: 2
                            }}>
                                Common <span sx={styles.gold}>questions.</span>
                            </Typography>

                            <Typography sx={{
                                fontSize: "0.95rem",
                                color: "#3d2e00",
                                maxWidth: "480px",
                                lineHeight: 1.72,
                                fontWeight: 300,
                                mb: 6
                            }}>
                                Quick answers to what teams ask us most.
                            </Typography>

                            <Box>
                                {faqs.map((faq, i) => (
                                    <Box key={i} sx={{
                                        borderBottom: "2px solid #f0d060",
                                        pb: 3,
                                        cursor: "pointer",
                                        "&:last-child": { borderBottom: "none" }
                                    }} onClick={() => toggleFaq(i)}>
                                        <Box sx={{
                                            ...styles.unbounded,
                                            fontSize: "0.85rem",
                                            fontWeight: 700,
                                            color: "#1c1400",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            letterSpacing: "-0.02em",
                                            lineHeight: 1.4,
                                            py: 2.5
                                        }}>
                                            {faq.q}
                                            <Box sx={{
                                                fontSize: "1.4rem",
                                                color: "#f59e0b",
                                            }}>
                                                {openFaq === i ? "−" : "+"}
                                            </Box>
                                        </Box>
                                        <Box sx={{
                                            fontSize: "0.83rem",
                                            color: "#3d2e00",
                                            lineHeight: 1.65,
                                            fontWeight: 300,
                                            maxHeight: openFaq === i ? "200px" : "0",
                                            overflow: "hidden",
                                            pt: openFaq === i ? 1.5 : 0,
                                            transition: "all .3s"
                                        }}>
                                            {faq.a}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Container>
                    </Box> */}

                    {/* FINAL CTA */}
                    <Box sx={{
                        backgroundColor: "#fde047",
                        borderTop: "2px solid #f0d060",
                        textAlign: "center",
                        py: 12
                    }}>
                        <Container>
                            <Typography sx={{
                                ...styles.unbounded,
                                fontSize: { xs: "2rem", md: "3.5rem" },
                                fontWeight: 900,
                                mb: 2,
                                letterSpacing: "-0.04em"
                            }}>
                                Ready to simplify<br />visitor management?
                            </Typography>
                            <Typography sx={{
                                fontSize: "0.95rem",
                                color: "#3d2e00",
                                maxWidth: "420px",
                                mx: "auto",
                                mb: 4,
                                lineHeight: 1.7,
                                fontWeight: 300
                            }}>
                                Start managing visitors, residents and facility operations
                                from one unified platform.
                            </Typography>
                            {!isAuthenticated && (
                                <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                                    <Button
                                        sx={{
                                            p: "15px 38px",
                                            borderRadius: "6px",
                                            backgroundColor: "#1c1400",
                                            border: "2px solid #1c1400",
                                            color: "#fde047",
                                            fontSize: "0.8rem",
                                            fontWeight: 700,
                                            letterSpacing: "-0.02em",
                                            fontFamily: "'Unbounded', sans-serif"
                                        }}
                                        onClick={() => navigate("/signup")}
                                    >
                                        START NOW
                                    </Button>
                                </Box>
                            )}
                            {/* <Button sx={{
                                    p: "14px 28px",
                                    borderRadius: "6px",
                                    backgroundColor: "transparent",
                                    border: "2px solid #1c1400",
                                    color: "#1c1400",
                                    fontSize: "0.9rem",
                                    fontWeight: 500,
                                    fontFamily: "'Epilogue', sans-serif"
                                }}>
                                    Request a demo
                                </Button> */}
                        </Container>
                    </Box>

                    {/* FOOTER */}
                    {/* <Box sx={{
                        backgroundColor: "#1c1400",
                        borderTop: "2px solid #f0d060",
                        p: "24px 5vw",
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.78rem",
                        color: "rgba(253,224,71,.35)"
                    }}>
                        <Typography>
                            © 2026 Maintain. Powered by <a href="#" style={{ color: "rgba(253,224,71,.35)", textDecoration: "underline" }}>omnevu.com</a>
                        </Typography>
                        <Typography>
                            By using this site you agree to our <a href="#" style={{ color: "rgba(253,224,71,.35)", textDecoration: "underline" }}>Terms & Privacy</a>
                        </Typography>
                    </Box> */}
                </>
            ) : (
                /* LOGIN PAGE */
                <Box sx={styles.loginWrap}>
                    {/* LOGIN LEFT */}
                    <Box sx={{
                        backgroundColor: "#fde047",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        p: { xs: "40px 7vw", md: "80px 7vw" },
                        position: "relative",
                        overflow: "hidden",
                        borderRight: "2px solid #f0d060"
                    }}>
                        <Box sx={{
                            position: "absolute",
                            width: "300px",
                            height: "300px",
                            borderRadius: "50%",
                            border: "40px solid rgba(202,138,4,.2)",
                            bottom: "-100px",
                            right: "-80px"
                        }} />
                        <Box sx={{ position: "relative", zIndex: 1 }}>
                            <Typography sx={{
                                ...styles.unbounded,
                                fontSize: { xs: "2rem", md: "2.5rem" },
                                fontWeight: 900,
                                color: "#1c1400",
                                lineHeight: 1.05,
                                letterSpacing: "-0.04em",
                                mb: 2
                            }}>
                                SIGN IN<br />TO YOUR<br />WORKSPACE.
                            </Typography>
                            <Typography sx={{
                                color: "#3d2e00",
                                fontSize: "0.9rem",
                                lineHeight: 1.65,
                                maxWidth: "320px",
                                fontWeight: 300,
                                mb: 5
                            }}>
                                Your assets, contracts, and maintenance schedules are one secure step away.
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                <Box sx={{
                                    backgroundColor: "rgba(255,255,255,.7)",
                                    border: "2px solid rgba(202,138,4,.25)",
                                    borderRadius: "10px",
                                    p: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between"
                                }}>
                                    <Box>
                                        <Typography sx={{
                                            ...styles.unbounded,
                                            fontSize: "0.7rem",
                                            fontWeight: 700,
                                            letterSpacing: "-0.01em"
                                        }}>
                                            TOTAL WARRANTIES
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.7rem", color: "#8a7a40", mt: 0.5 }}>
                                            Tracked right now
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        ...styles.unbounded,
                                        fontSize: "1.3rem",
                                        fontWeight: 900,
                                        color: "#ca8a04"
                                    }}>
                                        1,247
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    backgroundColor: "rgba(255,255,255,.7)",
                                    border: "2px solid rgba(202,138,4,.25)",
                                    borderRadius: "10px",
                                    p: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between"
                                }}>
                                    <Box>
                                        <Typography sx={{
                                            ...styles.unbounded,
                                            fontSize: "0.7rem",
                                            fontWeight: 700,
                                            letterSpacing: "-0.01em"
                                        }}>
                                            AMC SCHEDULES
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.7rem", color: "#8a7a40", mt: 0.5 }}>
                                            Active contracts
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        ...styles.unbounded,
                                        fontSize: "1.3rem",
                                        fontWeight: 900,
                                        color: "#ca8a04"
                                    }}>
                                        83
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    backgroundColor: "rgba(255,255,255,.7)",
                                    border: "2px solid rgba(202,138,4,.25)",
                                    borderRadius: "10px",
                                    p: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between"
                                }}>
                                    <Box>
                                        <Typography sx={{
                                            ...styles.unbounded,
                                            fontSize: "0.7rem",
                                            fontWeight: 700,
                                            letterSpacing: "-0.01em"
                                        }}>
                                            PLATFORM UPTIME
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.7rem", color: "#8a7a40", mt: 0.5 }}>
                                            Last 12 months
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        ...styles.unbounded,
                                        fontSize: "1.3rem",
                                        fontWeight: 900,
                                        color: "#ca8a04"
                                    }}>
                                        99.9%
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* LOGIN RIGHT */}
                    <Box sx={{
                        backgroundColor: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: { xs: "40px 6vw", md: "80px 6vw" }
                    }}>
                        <Box sx={{ width: "100%", maxWidth: "390px" }}>
                            <Typography sx={{
                                ...styles.unbounded,
                                fontSize: "1.6rem",
                                fontWeight: 900,
                                letterSpacing: "-0.04em",
                                color: "#1c1400",
                                mb: 1,
                                lineHeight: 1.15
                            }}>
                                WELCOME BACK.
                            </Typography>
                            <Typography sx={{
                                color: "#8a7a40",
                                fontSize: "0.85rem",
                                mb: 4,
                                fontWeight: 300
                            }}>
                                Enter your details below to access your dashboard.
                            </Typography>

                            <Button sx={{
                                width: "100%",
                                p: "13px",
                                borderRadius: "8px",
                                border: "2px solid #f0d060",
                                backgroundColor: "#fefce8",
                                color: "#1c1400",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                mb: 3,
                                transition: "all .2s",
                                "&:hover": {
                                    borderColor: "#f59e0b",
                                    backgroundColor: "#fefce8"
                                }
                            }}>
                                <Box component="svg" width="17" height="17" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </Box>
                                Continue with Google
                            </Button>

                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                mb: 3
                            }}>
                                <Box sx={{ flex: 1, height: "2px", backgroundColor: "#f0d060" }} />
                                <Typography sx={{ fontSize: "0.75rem", color: "#8a7a40" }}>
                                    or with email code
                                </Typography>
                                <Box sx={{ flex: 1, height: "2px", backgroundColor: "#f0d060" }} />
                            </Box>

                            <Box sx={{
                                backgroundColor: "#fefce8",
                                border: "2px solid #f0d060",
                                borderRadius: "10px",
                                p: 3
                            }}>
                                <Typography sx={{
                                    ...styles.unbounded,
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    textAlign: "center",
                                    color: "#1c1400",
                                    mb: 2,
                                    letterSpacing: "0.02em"
                                }}>
                                    PASSWORDLESS LOGIN
                                </Typography>
                                <Typography sx={{
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    color: "#3d2e00",
                                    mb: 1.5,
                                    display: "block"
                                }}>
                                    Your email address
                                </Typography>
                                <Input
                                    fullWidth
                                    type="email"
                                    placeholder="you@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    sx={{
                                        p: "12px 13px",
                                        borderRadius: "7px",
                                        border:
                                            "2px solid #f0d060",
                                        backgroundColor: "#ffffff",
                                        color: "#1c1400",
                                        fontSize: "0.875rem",
                                        outline: "none",
                                        mb: 2,
                                        "&:focus": {
                                            borderColor: "#f59e0b"
                                        },
                                        "&::placeholder": {
                                            color: "#8a7a40"
                                        }
                                    }}
                                />
                                <Button sx={{
                                    width: "100%",
                                    p: "13px",
                                    borderRadius: "7px",
                                    backgroundColor: "#1c1400",
                                    border: "2px solid #1c1400",
                                    color: "#fde047",
                                    fontSize: "0.8rem",
                                    fontWeight: 700,
                                    letterSpacing: "-0.02em",
                                    fontFamily: "'Unbounded', sans-serif",
                                    transition: "all .2s",
                                    "&:hover": {
                                        backgroundColor: "#f59e0b",
                                        borderColor: "#f59e0b",
                                        color: "#1c1400"
                                    }
                                }}>
                                    SEND LOGIN CODE
                                </Button>
                            </Box>

                            <Typography sx={{
                                textAlign: "center",
                                mt: 3,
                                fontSize: "0.78rem",
                                color: "#8a7a40"
                            }}>
                                New here? <a href="#" style={{ color: "#ca8a04", fontWeight: 600 }}>Create your account</a>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}

            <style jsx>{`
                @keyframes blink {
                    0%, 100% { opacity: 1 }
                    50% { opacity: 0.2 }
                }
                @keyframes scroll {
                    from { transform: translateX(0) }
                    to { transform: translateX(-50%) }
                }
            `}</style>
        </Box>
    );
};

export default HomePage;